import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";

export const FOCUS_FLASH_MS = 9000;

// Lien profond depuis la notification « nouveau signalement » (?focus_type=post&focus_id=…) : met l'élément en
// évidence, bascule sur son onglet réel si besoin (/admin/moderation/locate) — UNE SEULE FOIS — puis retire les
// paramètres de l'URL, sinon chaque clic sur « En attente » / « Masqués » rebasculait sur l'onglet de l'élément.
export function useModerationFocus({ items, status, setStatus, page = 1, setPage }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [focusedId, setFocusedId] = useState(null);
  const [locating, setLocating] = useState(false);
  const doneRef = useRef(false);
  const hintRef = useRef(false);
  const flashRef = useRef(null);
  const giveUpRef = useRef(null);

  useEffect(() => () => { clearTimeout(flashRef.current); clearTimeout(giveUpRef.current); }, []);

  useEffect(() => {
    const ft = searchParams.get("focus_type");
    const fid = searchParams.get("focus_id");
    if (!ft || !fid || doneRef.current) return;
    const finish = () => {
      doneRef.current = true;
      clearTimeout(giveUpRef.current);
      const next = new URLSearchParams(searchParams);
      next.delete("focus_type");
      next.delete("focus_id");
      next.delete("focus_status");
      next.delete("focus_page");
      setSearchParams(next, { replace: true });
    };
    const flashItem = (it) => {
      setFocusedId(it.id);
      const el = document.querySelector(`[data-testid="mod-item-${it.id}"]`);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
      clearTimeout(flashRef.current);
      flashRef.current = setTimeout(() => setFocusedId(null), FOCUS_FLASH_MS);
      finish();
    };

    const fstatus = searchParams.get("focus_status");
    const fpage = searchParams.get("focus_page");
    const hasHint = !!(fstatus || fpage);

    // Deep-link hint (from the « À traiter » screen): jump straight to the item's tab + page ONCE, so the queue
    // loads the RIGHT page directly. We then simply WAIT for the item to appear — never the fragile locate dance.
    if (hasHint && !hintRef.current) {
      hintRef.current = true;
      let changed = false;
      if (fstatus && fstatus !== status) { setStatus(fstatus); changed = true; }
      if (fpage && setPage && Number(fpage) !== page) { setPage(Number(fpage)); changed = true; }
      // Safety net: if the item never shows up (e.g. queue changed underneath us), stop waiting after a while.
      clearTimeout(giveUpRef.current);
      giveUpRef.current = setTimeout(finish, 12000);
      if (changed) return; // wait for the queue to reload on the hinted page
    }

    const found = items.find((it) => it.target_type === ft && String(it.target_id) === String(fid));
    if (found) { flashItem(found); return; }

    // With a page hint we just keep waiting for the hinted page to load — no premature locate() that would
    // clear the deep-link before the right page arrives.
    if (hasHint) return;

    // No hint (legacy notification deep-link): resolve the item's tab + page via the server, then retry.
    if (locating) return;
    setLocating(true);
    api.get(`/admin/moderation/locate?target_type=${encodeURIComponent(ft)}&target_id=${encodeURIComponent(fid)}`)
      .then(({ data }) => {
        const moved = (data?.status && data.status !== status) || (data?.page && setPage && data.page !== page);
        if (data?.status && data.status !== status) setStatus(data.status);   // l'onglet change → retrouvé au prochain rendu
        if (data?.page && setPage && data.page !== page) setPage(data.page);   // idem pour la page
        if (!moved && items.length) finish();                                   // déjà au bon endroit mais absent de la liste
      })
      .catch(() => { toast.info("Ce signalement est introuvable (contenu supprimé)."); finish(); })
      .finally(() => setLocating(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, searchParams]);

  return focusedId;
}
